import React, { memo } from 'react';
import { Star, Users, ShieldCheck, Shield } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { useCurrency } from '../contexts/CurrencyContext';
import { DigitalArchiveSpecs } from './DigitalArchiveSpecs';
import { BookSpineIcon, QuillNibIcon, ArchiveFolioIcon, SealedEnvelopeIcon, DirectAccessSealIcon, OwnershipKeyIcon } from './ui/ArchivalIcons';
export function OfferStack() {
  const {
    currency,
    formatPrice,
    isLoading
  } = useCurrency();
  const items = [{
    icon: BookSpineIcon,
    title: 'Complete eBook — 170 pages',
    detail: 'The full memorial, unabridged'
  }, {
    icon: QuillNibIcon,
    title: 'Premium editorial design',
    detail: 'Not a generic PDF — a crafted document'
  }, {
    icon: ArchiveFolioIcon,
    title: 'Archival-grade format',
    detail: 'Preserved for permanent reference'
  }, {
    icon: SealedEnvelopeIcon,
    title: 'Instant digital delivery',
    detail: 'Sealed and sent in seconds'
  }, {
    icon: DirectAccessSealIcon,
    title: 'Yours forever',
    detail: 'Download once, keep permanently'
  }, {
    icon: OwnershipKeyIcon,
    title: 'No subscriptions',
    detail: 'One payment, full ownership'
  }];
  return <section id="offer" className="py-20 md:py-28 px-6 bg-base" data-id="element-383">
      <div className="max-w-3xl mx-auto" data-id="element-384">
        {/* Section label */}
        <FadeIn data-id="element-385">
          <div className="flex items-center gap-4 mb-16" data-id="element-386">
            <div className="h-px bg-border-accent flex-grow" data-id="element-387" />
            <span className="font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary" data-id="element-388">
              ◆ The Offer ◆
            </span>
            <div className="h-px bg-border-accent flex-grow" data-id="element-389" />
          </div>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-390">
          <h2 className="font-display text-3xl md:text-5xl font-bold text-text-primary mb-4 text-center leading-tight" data-id="element-391">
            Everything You Need.
            <br data-id="element-392" />
            One Document.
          </h2>
          <p className="font-body text-lg text-text-secondary text-center mb-14 max-w-xl mx-auto" data-id="element-393">
            The complete Memorial Edition — crafted, archived, and yours to
            keep.
          </p>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-394">
          <div className="bg-surface border-2 border-border-accent rounded-sm shadow-gold-subtle overflow-hidden" data-id="element-395">
            {/* Premium feature grid */}
            <div className="p-8 md:p-10" data-id="element-396">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-397">
                {items.map((item, index) => {
                const Icon = item.icon;
                return <FadeIn key={index} delay={0.25 + index * 0.05} data-id="element-398">
                      <div className="flex items-start gap-4" data-id="element-399">
                        <div className="mt-0.5 flex-shrink-0" data-id="element-400">
                          <Icon className="text-accent-primary" size={22} data-id="element-401" />
                        </div>
                        <div data-id="element-402">
                          <p className="font-ui text-sm font-semibold text-text-primary leading-snug" data-id="element-403">
                            {item.title}
                          </p>
                          <p className="font-body text-xs text-text-muted mt-0.5" data-id="element-404">
                            {item.detail}
                          </p>
                        </div>
                      </div>
                    </FadeIn>;
              })}
              </div>
            </div>

            {/* Digital Archive Specifications */}
            <DigitalArchiveSpecs data-id="element-405" />

            {/* Price + CTA zone */}
            <div className="bg-elevated border-t border-border-accent p-8 md:p-12 text-center relative overflow-hidden" data-id="element-406">
              {/* Subtle gold glow */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-accent-primary/3 blur-3xl pointer-events-none" data-id="element-407" />

              <div className="relative z-10" data-id="element-408">
                <span className="inline-block font-ui text-xs font-semibold tracking-widest uppercase text-accent-primary border border-accent-primary/40 rounded-full px-5 py-1.5 mb-6" data-id="element-409">
                  ◆ Memorial Edition ◆
                </span>

                {/* Price with anchor */}
                <div className="mb-3" data-id="element-410">
                  <span className="font-body text-lg text-text-muted line-through mr-3" data-id="element-411">
                    {isLoading ? '$47' : formatPrice(47)}
                  </span>
                  <span className="font-display text-5xl md:text-7xl font-bold text-accent-primary" data-id="element-412">
                    {isLoading ? '$17' : formatPrice(17)}
                  </span>
                  <span className="font-ui text-lg text-text-muted ml-2" data-id="element-413">
                    {currency.code}
                  </span>
                </div>

                <p className="font-body text-text-secondary text-sm mb-8 italic" data-id="element-414">
                  One-time payment · Instant access · Yours forever
                </p>

                <Button href="https://www.mybetterlibrary.com/cart/42713853657165:1" className="w-full md:w-auto text-base md:text-lg px-12 py-5" data-id="element-415">
                  Buy Now for {isLoading ? '$17' : formatPrice(17)} &rarr;
                </Button>

                {/* Guarantee + security strip */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-6" data-id="element-416">
                  <span className="flex items-center gap-1.5 font-ui text-xs text-text-muted" data-id="element-417">
                    <Shield className="w-3.5 h-3.5 text-success" data-id="element-418" />
                    7-day money-back guarantee
                  </span>
                  <span className="hidden md:block text-border-default" data-id="element-419">·</span>
                  <span className="flex items-center gap-1.5 font-ui text-xs text-text-muted" data-id="element-420">
                    <ShieldCheck className="w-3.5 h-3.5 text-accent-primary" data-id="element-421" />
                    Secure checkout
                  </span>
                </div>

                {/* Payment methods */}
                <div className="flex items-center justify-center gap-3 mt-6" data-id="element-422">
                  {/* Visa */}
                  <svg className="h-6 w-auto text-text-primary opacity-80" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-423">
                    <rect width="48" height="32" rx="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" data-id="element-424" />
                    <path d="M19.5 21H17L18.75 11H21.25L19.5 21ZM15.5 11L13.1 18.1L12.8 16.6L12.8 16.6L11.9 12C11.9 12 11.8 11 10.5 11H6.1L6 11.2C6 11.2 7.5 11.5 9.2 12.5L11.4 21H14L18.5 11H15.5ZM38 21H40.5L38.3 11H36.3C35.2 11 34.9 11.8 34.9 11.8L31 21H33.5L34 19.5H37.1L38 21ZM34.8 17.5L36.2 13.5L37 17.5H34.8ZM30.5 13.5L30.8 11.8C30.8 11.8 29.5 11.3 28.1 11.3C26.6 11.3 23 12 23 15.2C23 18.2 27.2 18.2 27.2 19.8C27.2 21.3 23.5 21 22.2 20L21.9 21.8C21.9 21.8 23.2 22.5 25.2 22.5C27.2 22.5 30.4 21.3 30.4 18.4C30.4 15.4 26.2 15.1 26.2 13.8C26.2 12.5 29.1 12.7 30.5 13.5Z" fill="currentColor" fillOpacity="0.9" data-id="element-425" />
                  </svg>
                  {/* Mastercard */}
                  <svg className="h-6 w-auto text-text-primary opacity-80" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-426">
                    <rect width="48" height="32" rx="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" data-id="element-427" />
                    <circle cx="20" cy="16" r="8" fill="currentColor" fillOpacity="0.4" data-id="element-428" />
                    <circle cx="28" cy="16" r="8" fill="currentColor" fillOpacity="0.4" data-id="element-429" />
                    <path d="M24 10.5C25.8 12 27 14 27 16C27 18 25.8 20 24 21.5C22.2 20 21 18 21 16C21 14 22.2 12 24 10.5Z" fill="currentColor" fillOpacity="0.6" data-id="element-430" />
                  </svg>
                  {/* Amex */}
                  <svg className="h-6 w-auto text-text-primary opacity-80" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-431">
                    <rect width="48" height="32" rx="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" data-id="element-432" />
                    <text x="24" y="18" textAnchor="middle" fontSize="8" fontWeight="bold" fill="currentColor" fillOpacity="0.85" fontFamily="system-ui" data-id="element-433">
                      AMEX
                    </text>
                  </svg>
                  {/* PayPal */}
                  <svg className="h-6 w-auto text-text-primary opacity-80" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-434">
                    <rect width="48" height="32" rx="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" data-id="element-435" />
                    <text x="24" y="18" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor" fillOpacity="0.85" fontFamily="system-ui" data-id="element-436">
                      PayPal
                    </text>
                  </svg>
                  {/* Apple Pay */}
                  <svg className="h-6 w-auto text-text-primary opacity-80" viewBox="0 0 48 32" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-437">
                    <rect width="48" height="32" rx="4" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" data-id="element-438" />
                    <text x="24" y="18" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor" fillOpacity="0.85" fontFamily="system-ui" data-id="element-439">
                      Pay
                    </text>
                  </svg>
                </div>

                {/* Social proof strip */}
                <div className="mt-8 pt-6 border-t border-border-default space-y-4" data-id="element-440">
                  <div className="flex items-center justify-center gap-1" data-id="element-441">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-accent-primary fill-accent-primary" data-id="element-442" />)}
                    <span className="font-ui text-xs text-text-muted ml-2" data-id="element-443">
                      4.9/5
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-6 font-ui text-xs text-text-muted" data-id="element-444">
                    <span className="flex items-center gap-1.5" data-id="element-445">
                      <Users className="w-3.5 h-3.5 text-accent-primary" data-id="element-446" />
                      2,400+ readers
                    </span>
                  </div>
                  <p className="font-body text-sm text-text-secondary italic max-w-sm mx-auto leading-relaxed" data-id="element-447">
                    "This isn't just a book — it's the record that should have
                    existed from day one."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}